
import React from 'react';
import { Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ProfessionalCard from '@/components/ProfessionalCard';
import { ProfessionalInterface } from '@/services/professionals/types';
import { useTranslation } from 'react-i18next';
import { useDirection } from '@/providers/DirectionProvider';

interface SearchResultsProps {
  professionals: ProfessionalInterface[];
  sortOption: string;
  handleSortChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handlePhoneReveal: (professionalName: string) => boolean;
  resetFilters: () => void;
  // NEW: The profession that was searched for - pass to cards
  searchedProfessionId?: string;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  professionals,
  sortOption,
  handleSortChange,
  handlePhoneReveal,
  resetFilters,
  searchedProfessionId
}) => {
  const { t } = useTranslation();
  const { direction } = useDirection();

  return (
    <div className="flex-grow">
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-blue-700">
          {t('search.professionalsFound', { count: professionals.length })}
        </h2>
        <div className="flex items-center text-sm text-gray-500">
          <span>{t('search.sortBy')}: </span>
          <select
            className="appearance-none bg-transparent border-none px-1 font-medium focus:outline-none"
            value={sortOption}
            onChange={handleSortChange}
          >
            <option value="rating">{t('search.sortRating')}</option>
            <option value="popularity">{t('search.sortPopularity')}</option>
            <option value="name_asc">{t('search.sortNameAsc')}</option>
            <option value="name_desc">{t('search.sortNameDesc')}</option>
          </select>
        </div>
      </div>
      
      {professionals.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-animation">
          {professionals.map(professional => (
            <ProfessionalCard 
              key={professional.id}
              id={professional.id}
              name={professional.name}
              profession={professional.profession}
              rating={professional.rating || 0}
              reviewCount={professional.reviewCount || 0}
              location={professional.location}
              image={professional.image || 'https://images.unsplash.com/photo-1514539079130-25950c84af65?auto=format&fit=crop&q=80&w=800&h=450'}
              specialties={professional.specialties || []}
              phoneNumber={professional.phone || professional.phoneNumber}
              verified={professional.verified || professional.is_verified}
              profession_ids={professional.profession_ids}
              specialization_ids={professional.specialization_ids}
              sub_specializations={professional.sub_specializations}
              main_profession={professional.main_profession}
              searchedProfessionId={searchedProfessionId}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <Award className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('search.noResults')}</h3>
          <p className="text-gray-500 mb-6">
            {t('search.noResultsMessage')}
          </p>
          <Button
            variant="outline"
            onClick={resetFilters}
            className="border-teal-500 text-teal-500 hover:bg-teal-50"
          >
            {t('search.clearAllFilters')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SearchResults;
